import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Seo from '../components/seo/Seo.jsx';
import ContactCta from '../components/cta/ContactCta.jsx';
import FeatureCard from '../components/features/FeatureCard.jsx';
import ChatMock from '../components/chat/ChatMock.jsx';
import EmailMock from '../components/email/EmailMock.jsx';
import AppViewMock from '../components/app-view/AppViewMock.jsx';
import { FEATURES } from '../content/features.js';
import './FeaturePage.css';

/**
 * "Se hvordan" page for one feature: the problem, how Revetall does it (three steps next to
 * a chat, a warning email or an app screen, depending on the feature's `demo`),
 * contact, and links to the other features.
 * Texts come from `features.<id>`.
 * @param {Object} props
 * @param {{id: string, path: string, Illustration: Function, demo?: 'email'|'app'}} props.feature
 *   Entry from FEATURES
 * @returns {JSX.Element} Feature page
 */
function FeaturePage({ feature }) {
  const { t } = useTranslation();
  const key = `features.${feature.id}`;
  const title = t(`${key}.title`);
  const intro = t(`${key}.intro`);
  const steps = t(`${key}.steps`, { returnObjects: true });
  const demos = {
    email: () => <EmailMock email={t(`${key}.email`, { returnObjects: true })} />,
    app: () => <AppViewMock app={t(`${key}.app`, { returnObjects: true })} />,
    chat: () => <ChatMock chat={t(`${key}.chat`, { returnObjects: true })} />,
  };
  const demo = (demos[feature.demo] ?? demos.chat)();
  const otherFeatures = FEATURES.filter((other) => other.id !== feature.id);
  const { Illustration } = feature;

  return (
    <>
      <Seo title={`${title} – Revetall`} description={intro} />

      <section className="feature-hero">
        <div className="feature-hero-content">
          <div>
            <Link to="/" className="feature-back-link">
              <span aria-hidden="true">←</span> {t('featurePage.back')}
            </Link>
            <h1>{title}</h1>
          </div>
          <div className="feature-hero-art">
            <Illustration />
          </div>
        </div>
      </section>

      <section className="feature-section feature-problem">
        <div className="feature-narrow">
          <h2>{t('featurePage.problemTitle')}</h2>
          <p>{t(`${key}.problem`)}</p>
        </div>
      </section>

      <section className="feature-section">
        <div className="feature-how">
          <div>
            <h2>{t('featurePage.howTitle')}</h2>
            <ol className="feature-steps" role="list">
              {steps.map((step, index) => (
                <li key={step} className="feature-step">
                  <span className="feature-step-number" aria-hidden="true">
                    {index + 1}
                  </span>
                  <p>{step}</p>
                </li>
              ))}
            </ol>
          </div>
          <div className="feature-how-art">{demo}</div>
        </div>
      </section>

      <ContactCta />

      <section className="feature-section feature-more">
        <div className="feature-more-content">
          <h2>{t('featurePage.moreTitle')}</h2>
          <div className="feature-more-grid">
            {otherFeatures.map((other) => (
              <FeatureCard key={other.id} feature={other} />
            ))}
          </div>
        </div>
      </section>
    </>
  );
}

export default FeaturePage;
